import { isAxiosError } from 'axios'
import { push } from 'connected-react-router'
import { call, put, select, takeEvery } from 'redux-saga/effects'

import { Role } from '@app/domain/iam/user.interface'
import * as Storage from '@app/lib/storage'
import type { AppAction, AppState } from '@app/state'

import * as Config from '../config'
import * as User from '../user'

import * as OAuth from './oauth'
import {
  fetchCredentialsToken,
  requestPasswordReset,
  sendForgottenPasswordEmail,
  fetchOAuthToken,
} from './rest'
import * as Auth from './state'

import type {
  CredentialsTokenResponse,
  PasswordResetResponse,
  OAuthTokenResponse,
} from './interfaces'
import type { AxiosResponse} from 'axios';

function* handleAuthActiveDirectory() {
  yield takeEvery(Auth.ActionType.OAuth, authActiveDirectory)
}

function* authActiveDirectory(action: AppAction) {
  if (action.type === Auth.ActionType.OAuth) {
    try {
      const appState: AppState = yield select((a) => a)
      yield put(Auth.actions.oauthBegin())
      const codeResult = yield call(OAuth.startOAuth, appState.config.api)
      yield put(Auth.actions.oauthCodeReceived())
      const request = fetchOAuthToken(appState.config.api, codeResult.code)
      const result: AxiosResponse<OAuthTokenResponse> = yield call(
        request,
        appState.config.client.apiLocation,
      )
      yield put(
        Auth.actions.oauthSuccess(
          result.data.token,
          result.data['expiry_date'],
        ),
      )
    } catch (e) {
      if (isAxiosError(e)) {
        yield put(Auth.actions.oauthFailure(e.response?.data?.message))
      }
    }
  }
}

function* handleActiveDirectorySuccess() {
  yield takeEvery(Auth.ActionType.OAuthSuccess, authActiveDirectorySuccess)
}

function* authActiveDirectorySuccess(action: AppAction) {
  if (action.type === Auth.ActionType.OAuthSuccess) {
    yield call(Storage.setItem, Storage.API_TOKEN_VALUE, action.token, false)
    yield call(
      Storage.setItem,
      Storage.API_TOKEN_EXPIRY_DATE,
      `${action.expiryDate}`,
      false,
    )
    yield put(User.actions.getAccountInfo())
    yield put(Config.actions.fetchClinicConfig())
    yield put(push('/clinic/today'))
  }
}

function* handleAuthentication() {
  yield takeEvery(Auth.ActionType.Credentials, authSubmit)
}

function* authSubmit(action: AppAction) {
  if (action.type === Auth.ActionType.Credentials) {
    try {
      const appState: AppState = yield select((a) => a)
      yield put(Auth.actions.credentialsBegin())
      const request = fetchCredentialsToken(action.identifier, action.password)
      const response: AxiosResponse<CredentialsTokenResponse> = yield call(
        request,
        appState.config.apiLocation,
      )
      yield put(
        Auth.actions.credentialsSuccess(
          response.data.token,
          response.data.expiresOn,
          Role.Admin,
        ),
      )
    } catch (e) {
      if (isAxiosError(e)) {
        yield put(
          Auth.actions.credentialsFailure(e.response?.data?.message ?? ''),
        )
      }
    }
  }
}

function* handleAuthSuccess() {
  yield takeEvery(Auth.ActionType.CredentialsSuccess, authSuccess)
}

function* authSuccess(action: AppAction) {
  if (action.type === Auth.ActionType.CredentialsSuccess) {
    const appState: AppState = yield select((a) => a)
    yield call(
      Storage.setItem,
      Storage.API_USER_IDENTIFIER,
      appState.auth.identifier,
      false,
    )
    yield call(Storage.setItem, Storage.API_TOKEN_VALUE, action.token, false)
    yield call(
      Storage.setItem,
      Storage.API_TOKEN_EXPIRY_DATE,
      `${action.expiryDate}`,
      false,
    )
    yield call(Storage.setItem, Storage.AUTH_USER_ROLE, action.role, false)
    yield put(User.actions.getAccountInfo())
    yield put(Config.actions.fetchClinicConfig())
    yield put(push('/clinic/today'))
  }
}

function* handleForgotPassword() {
  yield takeEvery(Auth.ActionType.ForgotPassword, forgotPassword)
}

function* forgotPassword(action: AppAction) {
  if (action.type === Auth.ActionType.ForgotPassword) {
    try {
      const appState: AppState = yield select((a) => a)
      yield put(Auth.actions.forgotPasswordBegin())
      const request = sendForgottenPasswordEmail(action.email)
      yield call(request, appState.config.apiLocation)
      yield put(Auth.actions.forgotPasswordSuccess())
    } catch (e) {
      if (isAxiosError(e)) {
        yield put(
          Auth.actions.forgotPasswordFailure(
            e.response?.data?.message ??
              'We are unable to reset your password at this time. Please contact support.',
          ),
        )
      }
    }
  }
}

function* handleResetPassword() {
  yield takeEvery(Auth.ActionType.ResetPassword, resetPassword)
}

function* resetPassword(action: AppAction) {
  if (action.type === Auth.ActionType.ResetPassword) {
    try {
      const appState: AppState = yield select((a) => a)
      yield put(Auth.actions.resetPasswordBegin())
      const request = requestPasswordReset(action.requestId, action.password)
      const response: AxiosResponse<PasswordResetResponse> = yield call(
        request,
        appState.config.apiLocation,
      )
      if (response.data.status === 'success') {
        yield put(Auth.actions.resetPasswordSuccess())
      } else {
        yield put(
          Auth.actions.resetPasswordFailure(
            'There was an issue resetting your password. Please contact support.',
          ),
        )
      }
    } catch (e) {
      if (isAxiosError(e)) {
        yield put(
          Auth.actions.resetPasswordFailure(
            e.response?.data?.error ??
              'We are unable to reset your password at this time. Please contact support.',
          ),
        )
      }
    }
  }
}

function* handleAssumeRole() {
  yield takeEvery(Auth.ActionType.AssumeRole, assumeRole)
}

function* assumeRole(action: AppAction) {
  if (action.type === Auth.ActionType.AssumeRole) {
    yield call(Storage.setItem, Storage.AUTH_USER_ROLE, action.role, false)
  }
}

function* handleLogout() {
  yield takeEvery(Auth.ActionType.Logout, logout)
}

function* logout(action: AppAction) {
  if (action.type === Auth.ActionType.Logout) {
    yield call(Storage.reset, false)
    yield put(push('/'))
  }
}

export const sagas = {
  handleAuthActiveDirectory,
  handleActiveDirectorySuccess,
  handleAssumeRole,
  handleAuthentication,
  handleAuthSuccess,
  handleForgotPassword,
  handleResetPassword,
  handleLogout,
}
